import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ApiClient, type ExpertType } from "../core/api-client.js";
import { LeadMagnetWidgetBase } from "../core/widget-base.js";
import { widgetStyles } from "../core/widget-styles.js";
import { DEFAULT_IFI_COUNTRIES } from "../lead-magnets/ifi/countries.js";
import {
  ifiFormSchema,
  ifiStepSchemas,
} from "../lead-magnets/ifi/form-schema.js";
import { mapIfiFormToSubmitPayload } from "../lead-magnets/ifi/request-mapper.js";
import {
  getIfiFormProgress,
  getInitialIfiFormStep,
  getNextIfiFormStep,
  getPreviousIfiFormStep,
  isFranceCountry,
  type IfiCountryOption,
  type IfiFormModel,
  type IfiFormStep,
  type IfiResultModel,
  type IfiWidgetViewState,
} from "../lead-magnets/ifi/ui-state.js";

const COUNTRY_DATASET_URL =
  "https://raw.githubusercontent.com/datasets/country-list/main/data.csv";
const frenchRegionDisplayNames =
  typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["fr"], { type: "region" })
    : null;

@customElement("lead-magnet-widget")
export class LeadMagnetWidget extends LeadMagnetWidgetBase {
  @property({ type: String })
  public override slug = "ifi";

  @property({ type: String, attribute: "api-base-url" })
  public apiBaseUrl = "";

  @property({ type: String })
  public override source = "landing_ifi_widget";

  @state()
  private viewState: IfiWidgetViewState = "idle";

  @state()
  private step: IfiFormStep = getInitialIfiFormStep();

  @state()
  private form: IfiFormModel = {
    email: "",
    firstName: "",
    source: "landing_ifi_widget",
    country: "",
    consent: false,
    residencePrincipale: "",
    autresBiens: "",
    actifs: "",
    passifs: "",
    expertComptable: false,
    courtier: false,
    chasseurImmobilier: false,
    honeypot: "",
  };

  @state()
  private countries: IfiCountryOption[] = [];

  @state()
  private countrySearch = "";

  @state()
  private countryDropdownOpen = false;

  @state()
  private countryLoading = false;

  @state()
  private resultData: IfiResultModel | null = null;

  @state()
  private expertMessage = "";

  public static override styles = widgetStyles;

  public override connectedCallback(): void {
    super.connectedCallback();
    void this.loadCountries();
  }

  protected override render() {
    const integrationError = this.getIntegrationError();
    if (integrationError) {
      return html`<p class="error">${integrationError}</p>`;
    }

    const progress = getIfiFormProgress(this.step, this.form, this.resultData);
    const isIntroStep = this.step === "intro";
    const isContactStep = this.step === "contact";
    const isResultStep = this.step === "result";
    const isExpertStep = this.step === "expert-optin";
    const hasPrevious = [
      "country",
      "patrimoine-france",
      "actifs",
      "passifs",
      "contact",
    ].includes(this.step);

    return html`
      <form @submit=${this.onSubmit}>
        ${progress.current > 0
          ? html`<p class="step">
              Étape ${progress.current}/${progress.total}
            </p>`
          : null}
        ${this.renderCurrentStep()}

        <label style="display:none">
          Champ anti-bot
          <input
            name="honeypot"
            type="text"
            tabindex="-1"
            autocomplete="off"
            .value=${this.form.honeypot}
            @input=${(event: Event) =>
              this.updateField(
                "honeypot",
                (event.target as HTMLInputElement).value,
              )}
          />
        </label>

        ${this.errorMessage
          ? html`<p class="error">${this.errorMessage}</p>`
          : null}
        ${isExpertStep
          ? null
          : html`
              <div class="actions">
                ${hasPrevious
                  ? html`
                      <button
                        type="button"
                        class="secondary"
                        ?disabled=${this.isLoading}
                        @click=${this.onPreviousStep}
                      >
                        Précédent
                      </button>
                    `
                  : null}
                ${isContactStep
                  ? html`
                      <button ?disabled=${this.isLoading}>
                        ${this.isLoading
                          ? "Envoi en cours..."
                          : "Recevoir ma simulation gratuite"}
                      </button>
                    `
                  : isResultStep
                    ? html`
                        <button
                          type="button"
                          class="secondary"
                          ?disabled=${this.isLoading}
                          @click=${this.resetFlow}
                        >
                          Recommencer
                        </button>
                      `
                    : html`
                        <button
                          type="button"
                          ?disabled=${this.isLoading}
                          @click=${this.onNextStep}
                        >
                          ${isIntroStep ? "Commencer" : "Suivant"}
                        </button>
                      `}
              </div>
            `}
      </form>
    `;
  }

  private updateField(
    field: "email" | "firstName" | "source" | "country" | "honeypot",
    value: string,
  ): void {
    this.form = { ...this.form, [field]: value };
  }

  private updateConsent(value: boolean): void {
    this.form = { ...this.form, consent: value };
  }

  private updateNumberField(
    field: "residencePrincipale" | "autresBiens" | "actifs" | "passifs",
    value: string,
  ): void {
    const digits = value.replaceAll(/\D+/g, "");
    this.form = { ...this.form, [field]: digits };
  }

  private onNextStep = (): void => {
    if (!this.validateStep(this.step)) {
      this.viewState = "error";
      return;
    }

    this.trackStep(this.step);
    this.viewState = "idle";
    this.step = getNextIfiFormStep(this.step, this.form, this.resultData);
  };

  private onPreviousStep = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getPreviousIfiFormStep(this.step, this.form, this.resultData);
  };

  private onSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();

    if (this.step !== "contact") {
      this.onNextStep();
      return;
    }

    if (!this.validateStep("contact")) {
      this.viewState = "error";
      return;
    }

    const parsed = ifiFormSchema.safeParse(this.buildFinalValidationInput());
    if (!parsed.success) {
      this.viewState = "error";
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return;
    }

    this.startLoading();
    this.viewState = "submitting";

    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      const payload = mapIfiFormToSubmitPayload({
        ...this.form,
        source: parsed.data.source,
        consent: parsed.data.consent,
      });

      const response = await apiClient.submitLeadMagnet(
        "ifi",
        payload,
        this.makeIdempotencyKey(),
      );
      this.resultData = this.mapApiResult(response.result.payload);
      this.step = "result";
      this.viewState = "idle";
      this.errorMessage = "";
      this.trackSubmit();
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Soumission impossible.";
      this.viewState = "error";
      this.setError(msg);
      this.trackError(this.step, msg);
    } finally {
      this.stopLoading();
    }
  };

  private makeIdempotencyKey(): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `ifi_${crypto.randomUUID()}`;
    }
    return `ifi_${Date.now()}`;
  }

  private validateStep(step: IfiFormStep): boolean {
    if (step === "result" || step === "expert-optin") {
      this.errorMessage = "";
      return true;
    }

    const schema = ifiStepSchemas[step];
    const parsed = schema.safeParse(this.buildStepValidationInput(step));
    if (!parsed.success) {
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return false;
    }

    if (step === "contact") {
      this.form = {
        ...this.form,
        source: this.getSourceValue(),
      };
    }

    this.errorMessage = "";
    return true;
  }

  private buildStepValidationInput(step: IfiFormStep): Record<string, unknown> {
    if (step === "intro") {
      return {};
    }

    if (step === "contact") {
      return {
        email: this.form.email,
        firstName: this.form.firstName,
        source: this.getSourceValue(),
        consent: this.form.consent,
        honeypot: this.form.honeypot,
      };
    }

    if (step === "country") {
      return { country: this.form.country };
    }

    if (step === "patrimoine-france") {
      return {
        residencePrincipale: this.form.residencePrincipale,
        autresBiens: this.form.autresBiens,
      };
    }

    if (step === "actifs") {
      return { actifs: this.form.actifs };
    }

    if (step === "passifs") {
      return { passifs: this.form.passifs };
    }

    return {};
  }

  private buildFinalValidationInput(): Record<string, unknown> {
    return {
      ...this.form,
      source: this.getSourceValue(),
    };
  }

  private renderCurrentStep() {
    if (this.step === "intro") {
      return this.renderIntroStep();
    }

    if (this.step === "actifs") {
      return html`
        <label>
          Valeur du patrimoine immobilier en France
          <input
            name="actifs"
            type="text"
            inputmode="numeric"
            placeholder="1 300 000"
            .value=${this.formatThousands(this.form.actifs)}
            @input=${(event: Event) =>
              this.updateNumberField(
                "actifs",
                (event.target as HTMLInputElement).value,
              )}
          />
        </label>
        <p class="helper">
          Indique la valeur totale de ton patrimoine immobilier imposable en France.
        </p>
      `;
    }

    if (this.step === "country") {
      const options = this.getFilteredCountries();
      return html`
        <label class="country-box">
          Pays de residence
          <input
            name="country"
            type="text"
            placeholder="Commence à taper ton pays..."
            .value=${this.countrySearch}
            @focus=${() => {
              this.countryDropdownOpen = true;
            }}
            @blur=${() => {
              window.setTimeout(() => {
                this.countryDropdownOpen = false;
              }, 120);
            }}
            @input=${(event: Event) => {
              const value = (event.target as HTMLInputElement).value;
              this.countrySearch = value;
              this.countryDropdownOpen = true;
              this.form = { ...this.form, country: "" };
            }}
          />
          ${this.countryDropdownOpen
            ? html`
                <div class="country-options">
                  ${this.countryLoading
                    ? html`<div class="country-option">Chargement...</div>`
                    : options.length > 0
                      ? options.map(
                          (option) => html`
                            <button
                              type="button"
                              class="country-option"
                              @mousedown=${(event: Event) => {
                                event.preventDefault();
                              }}
                              @click=${() => this.selectCountry(option)}
                            >
                              <span>${option.name}</span>
                              <span class="country-code">${option.code}</span>
                            </button>
                          `,
                        )
                      : html`<div class="country-option">Aucun résultat</div>`}
                </div>
              `
            : null}
        </label>
      `;
    }

    if (this.step === "patrimoine-france") {
      return html`
        <label>
          Valeur de ta résidence principale
          <input
            name="residencePrincipale"
            type="text"
            inputmode="numeric"
            placeholder="900 000"
            .value=${this.formatThousands(this.form.residencePrincipale)}
            @input=${(event: Event) =>
              this.updateNumberField(
                "residencePrincipale",
                (event.target as HTMLInputElement).value,
              )}
          />
        </label>
        <label>
          Valeur de tes autres biens immobiliers
          <input
            name="autresBiens"
            type="text"
            inputmode="numeric"
            placeholder="400 000"
            .value=${this.formatThousands(this.form.autresBiens)}
            @input=${(event: Event) =>
              this.updateNumberField(
                "autresBiens",
                (event.target as HTMLInputElement).value,
              )}
          />
        </label>
      `;
    }

    if (this.step === "passifs") {
      return html`
        <label>
          Crédit restant
          <input
            name="passifs"
            type="text"
            inputmode="numeric"
            placeholder="300 000"
            .value=${this.formatThousands(this.form.passifs)}
            @input=${(event: Event) =>
              this.updateNumberField(
                "passifs",
                (event.target as HTMLInputElement).value,
              )}
          />
        </label>
        <p class="helper">
          Renseigne la valeur totale de ton crédit restant déductible.
        </p>
      `;
    }

    if (this.step === "contact") {
      return html`
        ${this.renderPreviewCard()}

        <p class="unlock-title">Débloque ta simulation complète</p>

        <label>
          Email
          <input
            name="email"
            type="email"
            placeholder="votre@email.com"
            .value=${this.form.email}
            @input=${(event: Event) =>
              this.updateField(
                "email",
                (event.target as HTMLInputElement).value,
              )}
            required
          />
        </label>

        <label>
          Prénom
          <input
            name="firstName"
            type="text"
            placeholder="Votre prénom"
            .value=${this.form.firstName}
            @input=${(event: Event) =>
              this.updateField(
                "firstName",
                (event.target as HTMLInputElement).value,
              )}
          />
        </label>

        <label style="display: flex; align-items: center; gap: 8px;">
          <input
            name="consent"
            type="checkbox"
            .checked=${this.form.consent}
            @change=${(event: Event) =>
              this.updateConsent((event.target as HTMLInputElement).checked)}
            required
          />
          <span>J'accepte de recevoir le résultat par e-mail.</span>
        </label>

        <div class="reassurance">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <span>Données sécurisées - Aucun spam, désabonnement en 1 clic</span>
        </div>
      `;
    }

    if (this.step === "result") {
      return this.renderResultStep();
    }

    if (this.step === "expert-optin") {
      return this.renderExpertStep();
    }

    return html``;
  }

  private renderIntroStep() {
    return html`
      <div class="intro-badge">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        Gratuit - 2 min
      </div>

      <h2 class="intro-title">
        Es-tu assujetti à l'imposition sur la fortune immobilière ?
      </h2>
      <p class="intro-copy">
        Simule ton imposition en quelques minutes. Clique et découvre comment
        optimiser ta situation.
      </p>

      <div class="intro-benefits">
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Calcul instantané de ton patrimoine net taxable</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Découvre si tu dépasses le seuil d'imposition</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Estimation du montant de ton IFI</span>
        </div>
      </div>

      <div class="intro-footer">
        <span>🔒 Tes données restent confidentielles</span>
      </div>
    `;
  }

  private renderPreviewCard() {
    const actifs = this.getActifsValue();
    const passifs = this.parseNumber(this.form.passifs);
    const net = Math.max(0, actifs - passifs);
    const seuil = 1300000;
    const isImposable = net > seuil;

    return html`
      <div class="preview-card">
        <div class="preview-row">
          <span class="preview-label">Patrimoine</span>
          <span class="preview-value">${this.formatCurrency(actifs)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Dette</span>
          <span class="preview-value">- ${this.formatCurrency(passifs)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Net taxable</span>
          <span class="preview-value">${this.formatCurrency(net)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Imposable à l'IFI ?</span>
          <span class="preview-value blurred"
            >${isImposable ? "OUI" : "NON"}<span class="lock">🔒</span></span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Montant estimé</span>
          <span class="preview-value blurred"
            >██ ███ €<span class="lock">🔒</span></span
          >
        </div>
      </div>
    `;
  }

  private parseNumber(value: string): number {
    const parsed = Number(value.replaceAll(/\s+/g, ""));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  }

  private formatThousands(value: string): string {
    const digits = value.replaceAll(/\D+/g, "").replace(/^0+(?=\d)/, "");
    if (!digits) {
      return "";
    }
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  private getActifsValue(): number {
    if (isFranceCountry(this.form.country)) {
      const residencePrincipale = this.parseNumber(
        this.form.residencePrincipale,
      );
      const autresBiens = this.parseNumber(this.form.autresBiens);
      return residencePrincipale * 0.7 + autresBiens;
    }
    return this.parseNumber(this.form.actifs);
  }

  private getSourceValue(): string {
    return this.source.trim() || this.form.source;
  }

  private async loadCountries(): Promise<void> {
    this.countryLoading = true;
    this.countries = normalizeCountriesForFrench(DEFAULT_IFI_COUNTRIES);
    try {
      const response = await fetch(COUNTRY_DATASET_URL);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const csv = await response.text();
      const parsedCountries = normalizeCountriesForFrench(
        parseCountriesCsv(csv),
      );
      if (parsedCountries.length >= DEFAULT_IFI_COUNTRIES.length) {
        this.countries = parsedCountries;
      }
    } catch {
      this.countries = normalizeCountriesForFrench(DEFAULT_IFI_COUNTRIES);
    } finally {
      this.countryLoading = false;
    }
  }

  private getFilteredCountries(): IfiCountryOption[] {
    const query = stripDiacritics(this.countrySearch.trim().toLowerCase());
    if (!query) {
      return this.countries.slice(0, 12);
    }
    return this.countries
      .filter((country) =>
        stripDiacritics(country.name.toLowerCase()).includes(query),
      )
      .slice(0, 12);
  }

  private selectCountry(country: IfiCountryOption): void {
    this.form = { ...this.form, country: country.name };
    this.countrySearch = country.name;
    this.countryDropdownOpen = false;
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    this.errorMessage = "";
  }

  private renderResultStep() {
    if (!this.resultData) {
      return html`
        <div class="result-card">
          <p class="result-title">Résultat indisponible</p>
          <p class="helper">
            Impossible d'afficher le résultat de la simulation pour le moment.
          </p>
        </div>
      `;
    }

    const resultClass = this.resultData.imposable ? "negative" : "positive";
    const totalBareme = this.resultData.bareme.reduce(
      (sum, row) => sum + row.montant,
      0,
    );
    return html`
      <div class="result-card">
        <p class="result-title ${resultClass}">
          ${this.resultData.imposable
            ? "Tu es imposable à l'IFI."
            : "Tu n'es pas imposable à l'IFI."}
        </p>
        <div class="preview-row">
          <span class="preview-label">Patrimoine net taxable</span>
          <span class="preview-value"
            >${this.formatCurrency(this.resultData.net)}</span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Seuil IFI</span>
          <span class="preview-value"
            >${this.formatCurrency(this.resultData.seuil)}</span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Montant estimé</span>
          <span class="preview-value"
            >${this.formatCurrency(this.resultData.montant)}</span
          >
        </div>
      </div>

      ${this.resultData.imposable
        ? html`
            <p
              class="result-title"
              style="margin-top: 0.75rem; font-size: 1rem;"
            >
              Simulation imposable (résultat)
            </p>
            ${this.resultData.bareme.length > 0
              ? html`
                  <table class="bareme-table">
                    <thead>
                      <tr>
                        <th>Tranche</th>
                        <th>Taux</th>
                        <th>Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${this.resultData.bareme.map(
                        (row) => html`
                          <tr>
                            <td>${row.tranche}</td>
                            <td>${row.taux}%</td>
                            <td>${this.formatCurrency(row.montant)}</td>
                          </tr>
                        `,
                      )}
                      <tr>
                        <td><strong>Total imposable</strong></td>
                        <td></td>
                        <td>
                          <strong>${this.formatCurrency(totalBareme)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                `
              : html`
                  <div
                    class="preview-row"
                    style="border-top: 2px solid #1674ee; padding-top: 8px;"
                  >
                    <span class="preview-label"
                      ><strong>Total imposable</strong></span
                    >
                    <span class="preview-value"
                      ><strong
                        >${this.formatCurrency(this.resultData.montant)}</strong
                      ></span
                    >
                  </div>
                `}
          `
        : null}
      ${this.resultData.imposable
        ? this.renderExpertSection()
        : this.renderNonImposableSuggestions()}
    `;
  }

  private renderExpertStep() {
    return this.renderExpertSection();
  }

  private renderExpertSection() {
    return html`
      <div class="expert-card" style="margin-top: 0.75rem;">
        <span class="expert-eyebrow">Accompagnement</span>
        <p class="expert-title">
          Tu veux être accompagné pour optimiser ton IFI ?
        </p>
        <p class="expert-copy">
          Sélectionne les experts que tu souhaites solliciter. Tu peux en
          choisir plusieurs.
        </p>

        <div class="expert-options">
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.expertComptable}
              @change=${() => {
                this.form = {
                  ...this.form,
                  expertComptable: !this.form.expertComptable,
                };
              }}
            />
            <span>Expert-comptable</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.courtier}
              @change=${() => {
                this.form = { ...this.form, courtier: !this.form.courtier };
              }}
            />
            <span>Courtier</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.chasseurImmobilier}
              @change=${() => {
                this.form = {
                  ...this.form,
                  chasseurImmobilier: !this.form.chasseurImmobilier,
                };
              }}
            />
            <span>Chasseur immobilier</span>
          </label>
        </div>

        <button
          type="button"
          ?disabled=${this.isLoading}
          @click=${this.submitExpertOptin}
        >
          ${this.isLoading
            ? "Envoi en cours..."
            : "Être mis en relation avec ces experts"}
        </button>

        ${this.expertMessage
          ? html`<p
              class="expert-feedback ${this.expertMessage.startsWith(
                "Sélectionne",
              )
                ? "error"
                : ""}"
            >
              ${this.expertMessage}
            </p>`
          : null}
        ${this.expertMessage && !this.expertMessage.startsWith("Sélectionne")
          ? html`
              <button
                type="button"
                class="expert-reset"
                ?disabled=${this.isLoading}
                @click=${this.resetFlow}
              >
                Recommencer
              </button>
            `
          : null}
      </div>
    `;
  }

  private renderNonImposableSuggestions() {
    return html`
      <div class="expert-card" style="margin-top: 0.75rem;">
        <span class="expert-eyebrow">Pour aller plus loin</span>
        <p class="expert-title">
          Tu peux aussi être mis en relation avec les partenaires recommandés
          par la communauté.
        </p>
        <p class="expert-copy">
          Selon ton projet, Immoneos peut t'orienter vers un expert de confiance
          : courtier, un chasseur immobilier ou un expert-comptable.
        </p>
        <div class="expert-options">
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.expertComptable}
              @change=${() => {
                this.form = {
                  ...this.form,
                  expertComptable: !this.form.expertComptable,
                };
              }}
            />
            <span>Expert-comptable</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.courtier}
              @change=${() => {
                this.form = { ...this.form, courtier: !this.form.courtier };
              }}
            />
            <span>Courtier</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.chasseurImmobilier}
              @change=${() => {
                this.form = {
                  ...this.form,
                  chasseurImmobilier: !this.form.chasseurImmobilier,
                };
              }}
            />
            <span>Chasseur immobilier</span>
          </label>
        </div>
        <button
          type="button"
          ?disabled=${this.isLoading}
          @click=${this.submitExpertOptin}
        >
          ${this.isLoading
            ? "Envoi en cours..."
            : "Être mis en relation avec ces experts"}
        </button>
        ${this.expertMessage
          ? html`<p
              class="expert-feedback ${this.expertMessage.startsWith(
                "Sélectionne",
              )
                ? "error"
                : ""}"
            >
              ${this.expertMessage}
            </p>`
          : null}
        ${this.expertMessage && !this.expertMessage.startsWith("Sélectionne")
          ? html`
              <button
                type="button"
                class="expert-reset"
                ?disabled=${this.isLoading}
                @click=${this.resetFlow}
              >
                Recommencer
              </button>
            `
          : null}
      </div>
    `;
  }

  private submitExpertOptin = async (): Promise<void> => {
    const expertTypes = this.getExpertTypes();
    if (expertTypes.length === 0) {
      this.expertMessage =
        "Sélectionne au moins un expert pour être mis en relation.";
      return;
    }
    this.startLoading();
    this.errorMessage = "";
    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      await apiClient.submitExpertOptin("ifi", {
        email: this.form.email.trim(),
        firstName: this.form.firstName.trim() || undefined,
        source: this.getSourceValue(),
        expertTypes,
      });
      this.expertMessage =
        expertTypes.length > 0
          ? "Parfait, ta demande a bien été enregistrée."
          : "Merci, ta simulation est terminée.";
    } catch (error) {
      this.errorMessage =
        error instanceof Error ? error.message : "Soumission impossible.";
    } finally {
      this.stopLoading();
    }
  };

  private resetFlow = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getInitialIfiFormStep();
    this.resultData = null;
    this.countrySearch = "";
    this.countryDropdownOpen = false;
    this.expertMessage = "";
    this.form = {
      email: "",
      firstName: "",
      source: this.getSourceValue(),
      country: "",
      consent: false,
      residencePrincipale: "",
      autresBiens: "",
      actifs: "",
      passifs: "",
      expertComptable: false,
      courtier: false,
      chasseurImmobilier: false,
      honeypot: "",
    };
  };

  private getExpertTypes(): ExpertType[] {
    const expertTypes: ExpertType[] = [];
    if (this.form.expertComptable) expertTypes.push("expert_comptable");
    if (this.form.courtier) expertTypes.push("courtier");
    if (this.form.chasseurImmobilier) {
      expertTypes.push("chasseur_immobilier");
    }
    return expertTypes;
  }

  private mapApiResult(payload: Record<string, unknown>): IfiResultModel {
    const net =
      toNumber(payload.net) ??
      Math.max(0, this.getActifsValue() - this.parseNumber(this.form.passifs));
    const seuil = toNumber(payload.seuil) ?? 1300000;
    const imposable =
      typeof payload.imposable === "boolean" ? payload.imposable : net > seuil;
    const montant =
      toNumber(payload.montant) ??
      toNumber(payload.ifi) ??
      toNumber(payload.impot) ??
      0;
    const bareme = parseBareme(payload.bareme);

    return { imposable, seuil, net, montant, bareme };
  }

  private getIntegrationError(): string | null {
    if (this.slug !== "ifi") {
      return 'Ce widget supporte uniquement le slug "ifi".';
    }

    const baseUrl = this.apiBaseUrl.trim();
    if (!baseUrl) {
      return "Configuration invalide: l'attribut `api-base-url` est obligatoire.";
    }

    try {
      const parsed = new URL(baseUrl);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "Configuration invalide: `api-base-url` doit utiliser http ou https.";
      }
    } catch {
      return "Configuration invalide: `api-base-url` doit être une URL absolue.";
    }

    return null;
  }
}

function parseCountriesCsv(csv: string): IfiCountryOption[] {
  const lines = csv.split(/\r?\n/).filter((line) => line.trim().length > 0);
  const dataLines = lines.slice(1);
  const countries: IfiCountryOption[] = [];

  for (const line of dataLines) {
    const parsed = parseCsvLine(line);
    if (parsed.length < 2) {
      continue;
    }
    const name = parsed[0]?.trim();
    const code = parsed[1]?.trim().toUpperCase();
    if (!name || !code) {
      continue;
    }
    countries.push({ name, code });
  }

  return countries.sort((a, b) => a.name.localeCompare(b.name));
}

function normalizeCountriesForFrench(
  countries: IfiCountryOption[],
): IfiCountryOption[] {
  const uniqueByCode = new Map<string, IfiCountryOption>();
  for (const country of countries) {
    const code = country.code.trim().toUpperCase();
    if (!code) {
      continue;
    }
    uniqueByCode.set(code, {
      code,
      name: toFrenchCountryName(code, country.name),
    });
  }

  return Array.from(uniqueByCode.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "fr"),
  );
}

function toFrenchCountryName(code: string, fallback: string): string {
  const fromIntl = frenchRegionDisplayNames?.of(code);
  if (fromIntl && fromIntl.trim().length > 0 && fromIntl !== code) {
    return fromIntl;
  }
  return fallback;
}

function parseCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }
    current += char;
  }

  values.push(current);
  return values;
}

function parseBareme(value: unknown): IfiResultModel["bareme"] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((row) => {
      if (!row || typeof row !== "object") {
        return null;
      }
      const record = row as Record<string, unknown>;
      const tranche = typeof record.tranche === "string" ? record.tranche : "";
      const taux = toNumber(record.taux) ?? 0;
      const montant = toNumber(record.montant) ?? 0;
      if (!tranche) {
        return null;
      }
      return { tranche, taux, montant };
    })
    .filter((row): row is { tranche: string; taux: number; montant: number } =>
      Boolean(row),
    );
}

function stripDiacritics(text: string): string {
  return text.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "");
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value.replaceAll(/\s+/g, ""));
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

declare global {
  interface HTMLElementTagNameMap {
    "lead-magnet-widget": LeadMagnetWidget;
  }
}
